import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useDeleteEndpoint, useEndpointRequestCount } from '../../hooks/useEndpoints';
import { useFlag } from '../../hooks/useFeatureFlags';
import { Endpoint } from '../../types';
import { formatCount } from '../../utils/formatUtils';
import { endpointDisplayName } from '../../utils/endpointLabel';
import DeleteResourceControl from '../shared/DeleteResourceControl';

interface EndpointCardProps {
  endpoint: Endpoint;
}

export default function EndpointCard({ endpoint }: EndpointCardProps) {
  const deleteEndpoint = useDeleteEndpoint();
  const { data: requestCount, isLoading: isLoadingCount } = useEndpointRequestCount(endpoint.id);
  const queuedEnabled = useFlag('innovate.fe.endpoints.queued');
  const label = endpointDisplayName(endpoint);

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/endpoints/${endpoint.id}`} className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                {label}
              </h3>
              {/* Sync/async indicator is flag-gated (task 336); flag off → no indicator (everything is sync) */}
              {queuedEnabled && (
                endpoint.responseQueued ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200" title="Async/Queued">
                    Async
                  </span>
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" title="Sync" />
                )
              )}
              {endpoint.mcpExposed && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200" title="Exposed as MCP tool">
                  MCP
                </span>
              )}
            </div>
            {endpoint.description && endpoint.description.trim() !== label && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                {endpoint.description}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Created {new Date(endpoint.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {isLoadingCount ? (
              <span className="inline-block w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
            ) : requestCount !== undefined ? (
              <span>{formatCount(requestCount)} request{requestCount === 1 ? '' : 's'}</span>
            ) : null}
          </div>
        </div>
      </Link>

      {/* One delete affordance for the whole app (task 864): the flag, the
          dialog, the dependents preview, the mutation and the toast in one
          place. The inline two-step swap this replaces said nothing about
          what else used the resource, and offered no way back. */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <DeleteResourceControl
          flag="innovate.fe.services.delete"
          entityLabel="AI Service"
          name={label}
          id={endpoint.id}
          stopPropagation
          body={<p>Its URL stops answering immediately and its path is free to reuse. Its scenarios and generated interfaces go with it; its collaboration config and the agents it uses stay.</p>}
          onDelete={() => deleteEndpoint.mutateAsync(endpoint.id)}
        />
      </div>
    </div>
  );
}
